import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, Button, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, Stack, List, ListItem, ListItemText
} from '@mui/material';
import supabase from '../../../../../utils/supabase';

const GBIF_BACKBONE_KEY = 'd7dddbf4-2cf0-4f39-9b2a-bb099caae36c';
const CHECKLIST_BANK_KEY = 'e007cc4a-8704-449d-8829-bb209d26d6c8';

const wait = (ms) => new Promise(res => setTimeout(res, ms));

export default function ComparisonSyncPage() {
  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState('');
  const [checklistSource, setChecklistSource] = useState('gbif_backbone');
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    const fetchFamilies = async () => {
      const { data, error } = await supabase
        .from('scientific_names')
        .select('name_spell_valid, id') // idも取得して、後でcurrent_parent_idでフィルタリングできるようにする
        .eq('current_rank', 'family'); // 小文字に修正
      if (error) {
        setLogs(prev => [...prev, `Error fetching families: ${error.message}`]);
      } else {
        setFamilies(data.map(f => f.name_spell_valid).sort());
      }
    };
    fetchFamilies();
  }, []);

  const addLog = (message) => {
    setLogs(prev => [message, ...prev]);
  };

  const handleSync = useCallback(async () => {
    if (!selectedFamily) {
      addLog('Error: Please select a family to sync.');
      return;
    }

    setLoading(true);
    setLogs([]);
    addLog(`Starting sync for family "${selectedFamily}" with source "${checklistSource}"...`);

    try {
      // 1. Get the ID of the selected family
      const { data: parentFamily, error: parentError } = await supabase
        .from('scientific_names')
        .select('id')
        .eq('name_spell_valid', selectedFamily)
        .eq('current_rank', 'family')
        .single();

      if (parentError || !parentFamily) {
        throw new Error(`Could not find family "${selectedFamily}" in the database. ${parentError?.message || ''}`);
      }

      // 2. Fetch subfamilies using the parent family's ID
      const { data: subfamilies, error: sfError } = await supabase
        .from('scientific_names')
        .select('name_spell_valid, id')
        .eq('current_parent', parentFamily.id)
        .eq('current_rank', 'subfamily');

      if (sfError) throw new Error(`Failed to fetch subfamilies for family ID ${parentFamily.id}: ${sfError.message}`);
      addLog(`Found ${subfamilies.length} subfamilies in "${selectedFamily}".`);

      const sourceDatasetKey = checklistSource === 'gbif_backbone' ? GBIF_BACKBONE_KEY : CHECKLIST_BANK_KEY;

      for (const sf of subfamilies) {
        const subfamilyName = sf.name_spell_valid;
        addLog(`- Processing subfamily: ${subfamilyName}...`);

        try {
          // 3. Get all genera for this subfamily from our DB using the RPC function
          const { data: genera, error: generaError } = await supabase.rpc('get_descendant_taxa', {
            parent_id_in: sf.id,
            descendant_rank_in: 'genus'
          });

          if (generaError) {
            addLog(`  - DB Error fetching genera: ${generaError.message}`);
            continue;
          }

          if (!genera || genera.length === 0) {
            addLog(`  - No valid genera found in our DB for this subfamily. Saving zero counts.`);
            await supabase.from('taxapad_2016').upsert({
              family: selectedFamily, subfamily: subfamilyName, source: checklistSource, genera: 0, species: 0,
            }, { onConflict: 'family,subfamily,source' });
            continue;
          }

          addLog(`  - Found ${genera.length} genera in our DB. Matching against ${checklistSource}...`);

          let totalChecklistGenusCount = 0;
          let totalChecklistSpeciesCount = 0;
          let totalChecklistSubspeciesCount = 0;

          // 4. For each genus, query GBIF
          for (const genus of genera) {
            const genusName = genus.name_spell_valid;
            if (!genusName) continue;

            try {
              const matchUrl = `https://api.gbif.org/v1/species/match?name=${encodeURIComponent(genusName)}&rank=genus&datasetKey=${sourceDatasetKey}&strict=true`;
              const matchResponse = await fetch(matchUrl);

              if (!matchResponse.ok) {
                addLog(`    - Genus ${genusName}: GBIF match API failed (${matchResponse.status}).`);
                await wait(200); continue;
              }
              const matchData = await matchResponse.json();

              // --- Stricter matching logic ---
              if (
                matchData.matchType !== 'EXACT' ||
                matchData.rank !== 'GENUS' ||
                !matchData.usageKey
              ) {
                addLog(`    - Genus ${genusName}: No exact genus match found (matchType: ${matchData.matchType}, rank: ${matchData.rank}).`);
                await wait(200); continue;
              }

              totalChecklistGenusCount++;
              const taxonKey = matchData.usageKey;

              // Get species + subspecies count under this genus
              const speciesStatsUrl = `https://api.gbif.org/v1/species/search?highertaxon_key=${taxonKey}&rank=SPECIES&status=ACCEPTED&limit=0`;
              const subspeciesStatsUrl = `https://api.gbif.org/v1/species/search?highertaxon_key=${taxonKey}&rank=SUBSPECIES&status=ACCEPTED&limit=0`;

              const [speciesRes, subspeciesRes] = await Promise.all([fetch(speciesStatsUrl), fetch(subspeciesStatsUrl)]);
              const speciesData = await speciesRes.json();
              const subspeciesData = await subspeciesRes.json();
              const speciesCount = speciesData.count || 0;
              const subspeciesCount = subspeciesData.count || 0;

              totalChecklistSpeciesCount += speciesCount;
              totalChecklistSubspeciesCount += subspeciesCount;
              addLog(`    - Genus ${genusName}: Match found. Species: ${speciesCount}, Subspecies: ${subspeciesCount}.`);

            } catch (err) {
              addLog(`    - Genus ${genusName}: An error occurred: ${err.message}`);
            }
            await wait(250); // Be nice to the API
          }

          addLog(`  - Subfamily Total for ${subfamilyName}: ${totalChecklistGenusCount} genera, ${totalChecklistSpeciesCount} species, ${totalChecklistSubspeciesCount} subspecies.`);

          // 5. Upsert aggregated data for the subfamily
          const { error: upsertError } = await supabase
            .from('taxapad_2016')
            .upsert({
              family: selectedFamily, subfamily: subfamilyName, source: checklistSource,
              genera: totalChecklistGenusCount, species: totalChecklistSpeciesCount, subspecies: totalChecklistSubspeciesCount,
            }, {
              onConflict: 'family,subfamily,source'
            });

          if (upsertError) {
            addLog(`  - DB Error: Failed to save final stats. ${upsertError.message}`);
          } else {
            addLog(`  - Successfully saved final stats for ${subfamilyName}.`);
          }

        } catch (err) {
          addLog(`  - An error occurred while processing ${subfamilyName}: ${err.message}`);
        }
      }

      addLog('Sync finished!');
    } catch (error) {
      addLog(`An unexpected error occurred: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedFamily, checklistSource]);

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Checklist Comparison Data Sync
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          This tool fetches summary statistics (number of genera and species) for subfamilies from external checklists (GBIF, Checklist Bank) and saves them to the local database. This data is then used in the 'Comparison with Checklists' analysis chart.
        </Typography>
        <Alert severity="warning" sx={{ mb: 3 }}>
          This process can take a long time depending on the number of subfamilies. Please run it one family at a time. The process makes numerous requests to the GBIF API, so use it sparingly.
        </Alert>

        <Stack spacing={3}>
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Checklist Source</InputLabel>
              <Select
                value={checklistSource}
                label="Checklist Source"
                onChange={(e) => setChecklistSource(e.target.value)}
                disabled={loading}
              >
                <MenuItem value="gbif_backbone">GBIF Backbone</MenuItem>
                <MenuItem value="checklist_bank">Checklist Bank</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Family</InputLabel>
              <Select
                value={selectedFamily}
                label="Family"
                onChange={(e) => setSelectedFamily(e.target.value)}
                disabled={loading}
              >
                {families.map(family => (
                  <MenuItem key={family} value={family}>{family}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              onClick={handleSync}
              disabled={loading || !selectedFamily}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Syncing...' : `Sync ${selectedFamily}`}
            </Button>
          </Box>

          {logs.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, maxHeight: '40vh', overflowY: 'auto', bgcolor: 'background.default' }}>
              <List dense>
                {logs.map((log, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemText primary={log} primaryTypographyProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}